import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../../../../components/AsyncStates';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { getStandings } from '@/api/TournamentManagement/tournamentApi';
import type { StandingEntry } from '@/models/tournament';

type SortField = 'points' | 'played' | 'won' | 'drawn' | 'lost' | 'goalsFor' | 'goalsAgainst' | 'goalDifference';

interface StandingsTabProps {
  tournamentId: number;
}

const StandingsTab: React.FC<StandingsTabProps> = ({ tournamentId }) => {
  const { t } = useTranslation('tournament');
  const [standings, setStandings] = useState<StandingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('points');
  const [sortDesc, setSortDesc] = useState(true);

  const sortedStandings = useMemo(() => {
    return [...standings].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const diff = sortDesc ? bVal - aVal : aVal - bVal;
      if (diff !== 0) return diff;
      // Tiebreaker: goal difference desc, then goals for desc
      if (sortField !== 'goalDifference') {
        const gdDiff = b.goalDifference - a.goalDifference;
        if (gdDiff !== 0) return gdDiff;
      }
      return b.goalsFor - a.goalsFor;
    });
  }, [standings, sortField, sortDesc]);

  const isDefaultSort = sortField === 'points' && sortDesc;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(true);
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <Icon name="ArrowUpDown" size={12} className="inline ms-1 opacity-30" />;
    return <Icon name={sortDesc ? 'ArrowDown' : 'ArrowUp'} size={12} className="inline ms-1 text-primary" />;
  };

  const fetchStandings = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getStandings(tournamentId);
      if (result.errors.length > 0) {
        setError(result.errors[0]?.description || t('Failed to load standings'));
      } else {
        setStandings(result.payload ?? []);
      }
    } catch {
      setError(t('Failed to load standings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStandings();
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="py-12">
        <LoadingState title={t('Loading standings...')} size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-start">
          <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 me-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
              {t('Error loading standings')}
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchStandings} className="mt-3">
              {t('Try Again')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (standings.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
        <Icon name="Trophy" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">{t('No standings data yet')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('Standings will appear once matches are completed.')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="standings-table">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="px-4 py-3 text-start font-semibold text-muted-foreground w-12">#</th>
              <th className="px-4 py-3 text-start font-semibold text-muted-foreground">{t('Team')}</th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('played')}>{t('Played')}<SortIcon field="played" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('won')}>{t('Won')}<SortIcon field="won" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('drawn')}>{t('Drawn')}<SortIcon field="drawn" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('lost')}>{t('Lost')}<SortIcon field="lost" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('goalsFor')}>{t('Goals For')}<SortIcon field="goalsFor" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('goalsAgainst')}>{t('Goals Against')}<SortIcon field="goalsAgainst" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('goalDifference')}>{t('Goal Difference')}<SortIcon field="goalDifference" /></th>
              <th className="px-4 py-3 text-center font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('points')}>{t('Points')}<SortIcon field="points" /></th>
            </tr>
          </thead>
          <tbody>
            {sortedStandings.map((entry, index) => {
              // Show original league rank (based on points order) regardless of current sort
              const originalRank = isDefaultSort
                ? index + 1
                : standings.findIndex(s => s.teamName === entry.teamName) + 1;
              return (
              <tr
                key={entry.teamName}
                className={`border-b border-border last:border-b-0 transition-colors hover:bg-muted/30 ${
                  index % 2 === 1 ? 'bg-muted/10' : ''
                }`}
                data-testid={`standing-row-${index}`}
              >
                <td className="px-4 py-3 font-bold text-muted-foreground">{originalRank}</td>
                <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{entry.teamName}</td>
                <td className="px-4 py-3 text-center text-foreground">{entry.played}</td>
                <td className="px-4 py-3 text-center text-green-600 dark:text-green-400 font-medium">{entry.won}</td>
                <td className="px-4 py-3 text-center text-amber-600 dark:text-amber-400">{entry.drawn}</td>
                <td className="px-4 py-3 text-center text-red-600 dark:text-red-400">{entry.lost}</td>
                <td className="px-4 py-3 text-center text-foreground">{entry.goalsFor}</td>
                <td className="px-4 py-3 text-center text-foreground">{entry.goalsAgainst}</td>
                <td className="px-4 py-3 text-center font-medium text-foreground">
                  {entry.goalDifference > 0 ? `+${entry.goalDifference}` : entry.goalDifference}
                </td>
                <td className="px-4 py-3 text-center font-bold text-primary">{entry.points}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StandingsTab;
